import { computed, inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { useStore } from 'ngx-unificator/store';
import { NotificationFieldsResolverService } from './notification-fields-resolver.service';
import { BonusStatus } from '../../../services/user/data/user-bonuses.data';
import { PlayerBonusChangedEvent } from '../../../types/sse.type';
import { ServerSentEventsService } from '../../../services/server-sent-events.service';
import { NotificationStoreEntity, NotificationType } from '../../../store/notification.store';
import { UserStoreEntity } from '../../../store/user.store';

@Injectable({
  providedIn: 'root',
})
export class NotificationCenterService {
  private _router = inject(Router);
  private _sse = inject(ServerSentEventsService);
  private _notificationFields = inject(NotificationFieldsResolverService);

  private _notificationStore = useStore<NotificationStoreEntity>('NOTIFICATION_STORE');
  private _userStore = useStore<UserStoreEntity>('USER_STORE');

  public notificationList = computed(() => {
    if (!this._userStore.storeSignal().isAuth) {
      return [];
    }

    return this._notificationStore.storeSignal().list?.filter((notif) => !notif?.isDeleted) || [];
  });
  public animateBell = computed(() => this._userStore.storeSignal().isAuth && this._notificationStore.storeSignal().animateBell);

  public dropdownOpened = signal(false);

  public listenSseNotifications() {
    this._sse.bonusChanged$.subscribe(async (data: PlayerBonusChangedEvent) => {
      if (data?.data?.status === BonusStatus.NEW) {
        this.clearExpiredNotifications();
        await this.addNotifToStore(data?.data);
      }
    });
  }

  public async addNotifToStore(notif: unknown) {
    if (!this._userStore.storeSignal().isAuth) {
      return;
    }

    const notification = await this._notificationFields.resolveNotificationFields(notif);

    if (notification) {
      const list = this._notificationStore.storeSignal().list || [];

      const alreadyExistedNotif = list.find((item) => item?.id === notification?.id);
      notification.deleteTimestamp = alreadyExistedNotif?.deleteTimestamp;

      if (alreadyExistedNotif?.isDeleted) {
        notification.isDeleted = true;
      } else {
        if (!alreadyExistedNotif) {
          notification.deleteTimestamp = this._getEndOfDayTimestamp();
        }
      }

      const newList = list.filter((item) => item?.id !== notification?.id);

      this._notificationStore.storeSignal.update((state) => ({
        ...state,
        list: [...newList, notification],
        animateBell: true,
      }));

      setTimeout(() => {
        this._notificationStore.storeSignal.update((state) => ({
          ...state,
          animateBell: false,
        }));
      }, 500);
    }
  }

  private _getEndOfDayTimestamp(): number {
    const now = new Date();
    const endOfDayUTC = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 23, 59, 59, 999));

    return endOfDayUTC.getTime();
  }

  public clearExpiredNotifications() {
    if (!this._userStore.storeSignal().isAuth) {
      return;
    }

    const list = this._notificationStore
      .storeSignal()
      .list?.map((notification) => {
        return Date.now() < notification?.deleteTimestamp || !notification?.deleteTimestamp ? notification : { ...notification, isDeleted: true };
      })
      .filter((notification) => {
        const startOfToday = new Date();
        startOfToday.setHours(0, 0, 0, 0);
        const isBeforeToday = notification?.deleteTimestamp < startOfToday.getTime();
        return !isBeforeToday;
      });

    this._notificationStore.storeSignal.update((state) => ({
      ...state,
      list,
    }));
  }

  public clearNotificationStore() {
    this._notificationStore.storeSignal.update((state) => ({
      ...state,
      list: [],
      animateBell: false,
    }));
  }

  public removeNotify(notification: NotificationType) {
    this._notificationStore.storeSignal.update((state) => ({
      ...state,
      list: state.list.map((notif) => {
        if (notif?.id === notification?.id) {
          return { ...notif, isDeleted: true };
        } else {
          return notif;
        }
      }),
    }));
  }

  public onNotificationClick(notification: NotificationType) {
    if (!notification.isPinned) {
      this.removeNotify(notification);
    }

    if (notification.link) {
      const link = {
        commands: [notification?.link?.split('?')?.[0]],
        queryParams: this._parseQueryString(notification?.link?.split('?')?.[1]),
      };

      this._router.navigate(link.commands, {
        queryParams: link.queryParams,
        queryParamsHandling: 'merge',
      });
    }
  }

  private _parseQueryString(queryString) {
    return queryString
      ?.split('&')
      ?.map((param) => param.split('='))
      ?.reduce((acc, [key, value]) => {
        acc[key] = decodeURIComponent(value);
        return acc;
      }, {});
  }

  /**
   * Open dropdown menu
   */
  open() {
    this._notificationStore.storeSignal.update((state) => ({
      ...state,
      animateBell: false,
    }));
    this.dropdownOpened.set(true);
  }

  /**
   * Close dropdown menu
   */
  close() {
    this.dropdownOpened.set(false);
  }

  /**
   * Toggle dropdown menu
   */
  toggle() {
    this.dropdownOpened() ? this.close() : this.open();
  }
}
